import { useEffect, useId, useRef, useState } from 'react';

import { Banner, Button, Flex, Text } from '@spotify-internal/encore-web';
import { useDisclosure } from '@spotify/confidence-core-react';
import {
  ObservationsV1IssueAutonomyLevel,
  type ObservationsV1IssueFeedbackInput,
} from '@spotify/confidence-graphql';

import { FeedbackTray } from './FeedbackTray';
import { FinishReviewDialog } from './FinishReviewDialog';
import { ReviewDocument } from './ReviewDocument';
import {
  type DraftFeedback,
  type NewFeedback,
  buildIssueReviewFeedback,
} from './reviewFeedback';

import styles from './IssueProblemReview.module.css';

type ReviewMode = 'not_started' | 'reviewing' | 'paused';

const REVIEW_BUTTON_LABELS: Record<ReviewMode, string> = {
  not_started: 'Review',
  reviewing: 'Pause review',
  paused: 'Continue review',
};

export type IssueProblemReviewProps = {
  canEdit: boolean;
  documentName: string;
  generalFeedback: string;
  isSubmitting: boolean;
  currentAutonomyLevel?: ObservationsV1IssueAutonomyLevel | null;
  onFinishReview: (input: {
    autonomyLevel: ObservationsV1IssueAutonomyLevel;
    feedback: ObservationsV1IssueFeedbackInput[];
  }) => Promise<boolean>;
};

export const IssueProblemReview = ({
  canEdit,
  documentName,
  generalFeedback,
  isSubmitting,
  currentAutonomyLevel,
  onFinishReview,
}: IssueProblemReviewProps) => {
  const [reviewMode, setReviewMode] = useState<ReviewMode>('not_started');
  const [finishReviewOpen, finishReviewDisclosure] = useDisclosure();
  const [feedbackItems, setFeedbackItems] = useState<DraftFeedback[]>([]);
  const feedbackIdRef = useRef(0);
  const reviewRootRef = useRef<HTMLDivElement>(null);
  const problemHeadingId = useId();
  const reviewing = reviewMode === 'reviewing';
  const feedbackCount = feedbackItems.length + (generalFeedback.trim() ? 1 : 0);
  useEffect(() => {
    if (!reviewing || finishReviewOpen) {
      return undefined;
    }

    const exitReviewOnEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setReviewMode('paused');
      }
    };

    document.addEventListener('keydown', exitReviewOnEscape);
    return () => document.removeEventListener('keydown', exitReviewOnEscape);
  }, [finishReviewOpen, reviewing]);

  const addFeedback = (feedback: NewFeedback): void => {
    feedbackIdRef.current += 1;
    setFeedbackItems(currentFeedback => [
      ...currentFeedback,
      {
        ...feedback,
        id: `issue-feedback-${feedbackIdRef.current}`,
      },
    ]);
  };

  const removeFeedback = (id: string): void => {
    setFeedbackItems(currentFeedback =>
      currentFeedback.filter(item => item.id !== id),
    );
  };

  const finishReview = async (
    autonomyLevel: ObservationsV1IssueAutonomyLevel,
  ): Promise<boolean> => {
    const didFinish = await onFinishReview({
      autonomyLevel,
      feedback: buildIssueReviewFeedback(feedbackItems, generalFeedback),
    });

    if (didFinish) {
      finishReviewDisclosure.close();
      setFeedbackItems([]);
      setReviewMode('not_started');
    }

    return didFinish;
  };

  return (
    <div ref={reviewRootRef} className={styles.reviewRoot}>
      <Banner
        contextual
        colorSet="positiveSubdued"
        iconAriaLabel="Review required:"
      >
        <Flex direction="column" alignItems="start" gap="tighter-4">
          <Text as="p" variant="bodySmallBold">
            Issue waiting for review
          </Text>
          <Text as="p" variant="bodySmall">
            Review the issue and provide feedback before Confidence can
            continue.
          </Text>
          {canEdit && (
            <Button
              type="button"
              variant={reviewing ? 'secondary-bordered' : 'primary'}
              colorSet="positive"
              size="small"
              className={styles.reviewButton}
              onClick={() =>
                setReviewMode(currentMode =>
                  currentMode === 'reviewing' ? 'paused' : 'reviewing',
                )
              }
             data-track-source-anchor="0579ccf972;issue-problem-review-button">
              {REVIEW_BUTTON_LABELS[reviewMode]}
            </Button>
          )}
        </Flex>
      </Banner>
      <Text id={problemHeadingId} as="h3" variant="titleExtraSmall">
        Problem
      </Text>
      <ReviewDocument
        documentName={documentName}
        feedbackItems={feedbackItems}
        problemHeadingId={problemHeadingId}
        reviewing={reviewing}
        onAdd={addFeedback}
      />
      {reviewing && (
        <FeedbackTray
          anchorRef={reviewRootRef}
          feedbackCount={feedbackCount}
          feedbackItems={feedbackItems}
          isSubmitting={isSubmitting}
          onFinish={finishReviewDisclosure.open}
          onRemove={removeFeedback}
        />
      )}
      {finishReviewOpen && (
        <FinishReviewDialog
          currentAutonomyLevel={currentAutonomyLevel}
          feedbackCount={feedbackCount}
          hasReviewNotes={feedbackCount > 0}
          isSubmitting={isSubmitting}
          onClose={finishReviewDisclosure.close}
          onSubmit={finishReview}
         data-track-source-anchor="3fe91b84e6;issue-problem-review-finish-review-dialog"/>
      )}
    </div>
  );
};
